/**
 * 작성자 : AI Assistant
 * 날짜 : 2024.12.19
 * 기능 : 이미지 URL 입력을 위한 모달 컴포넌트
 */

import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BtnBlue, BtnGray } from '../Button';
import { IModal } from 'customTypes';
import { apiPost } from '../../services/_common';
import { toast } from 'react-toastify';

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5010;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 60%);
  backdrop-filter: blur(0.5rem);
  user-select: none;

  > .modal {
    width: 30rem;
    max-width: calc(100vw - 2rem);
    border-radius: 0.5rem;
    overflow: hidden;
    background-color: var(--board);
    color: var(--text-primary);

    > .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem;
      border-bottom: 1px solid var(--outline);
      color: var(--text-primary);

      > .title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem;
        font-size: 1.125rem;
        font-weight: 500;
      }

      > .closeButton {
        border-radius: 0.5rem;
        margin: 0.375rem;
        padding: 0.25rem;
        display: flex;
        cursor: pointer;

        > .material-symbols-rounded {
          font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
          font-size: 1.375rem;
          color: var(--text-primary);
        }

        &:hover {
          background-color: var(--primary);
          box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.1);

          .material-symbols-rounded {
            color: var(--tonal-deep-blue);
          }
        }
      }
    }

    > .body {
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;

      > .inputGroup {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        > .label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-secondary);
        }

        > .input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--outline);
          border-radius: 0.25rem;
          background-color: var(--board);
          color: var(--text-primary);
          font-size: 0.875rem;

          &:focus {
            outline: none;
            border-color: var(--primary);
            box-shadow: 0 0 0 2px rgba(66, 148, 255, 0.1);
          }

          &::placeholder {
            color: var(--text-tertiary);
          }
        }
      }
    }

    > .footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding: 1rem 1.5rem;
      border-top: 1px solid var(--outline);
      background-color: var(--board);
    }
  }
`;

interface Props {
  setOpenModal: Dispatch<SetStateAction<IModal>>;
  refetch: () => void;
}

const TestLLMModal = ({ setOpenModal, refetch }: Props) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (imageUrl.trim()) {
      setLoading(true);
      const res = await apiPost({ path: '/llm/fireDetectionAnalysis', req: { imageUrl } });
      if (res.status === 200) {
        toast.success(t('전송이 완료되었습니다.'));
        refetch();
        setOpenModal({ status: false, type: '' });
      } else {
        toast.error(t(res.data.message));
      }
      setLoading(false);
    }
  };

  const handleClose = () => {
    setImageUrl('');
    setOpenModal({ status: false, type: '' });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <Root onClick={handleBackdropClick} role='presentation'>
      <div
        className='modal'
        role='presentation'
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className='header'>
          <div className='title'>{t('테스트 LLM')}</div>
          <div className='closeButton' onClick={handleClose} role='presentation'>
            <span className='material-symbols-rounded'>close</span>
          </div>
        </div>

        <div className='body'>
          <div className='inputGroup'>
            <label className='label' htmlFor='imageUrl'>
              {t('이미지 URL')}
            </label>
            <input id='imageUrl' type='url' className='input' value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder={t('이미지 URL을 입력해주세요')} />
          </div>
        </div>

        <div className='footer'>
          <BtnGray onClick={handleClose} disabled={loading}>
            {t('취소')}
          </BtnGray>
          <BtnBlue onClick={handleSubmit} disabled={!imageUrl.trim() || loading}>
            {loading ? t('전송중') : t('전송')}
          </BtnBlue>
        </div>
      </div>
    </Root>
  );
};

export default TestLLMModal;
